import type { ProtocolStepDraft } from '../schemas/protocol.js';

/**
 * The variables of §8.6, as a direction STORES them.
 *
 * A variable is stored as a TOKEN - `{{objectif:id}}`, `{{critere:id}}` - and
 * displayed with its current name, so a rename invalidates nothing. The two
 * kinds a protocol knows are the ones here: an objective (of the current step
 * or of a previous one), whose MATTER the token renders in session, and a
 * criterion, whose DIRECTION it renders. `{{champ:nom}}` and `{{choix:nom}}`
 * belong to an experience's pages and are not a protocol's to store.
 *
 * ## Why this file is in `shared`
 *
 * Three readers parse the same tokens: the editor, which draws a chip per
 * token; the server's reference extractor, which lists the criteria a version
 * cites (§4, « utilisé dans » and the production check); and the engine, which
 * will resolve them when a text is served. One regular expression, written once,
 * is what keeps « what the editor inserted » and « what the server counts »
 * from drifting - the failure that would show up as a criterion retired without
 * warning because one side spelled the token differently.
 *
 * The French inside the token (`objectif`, `critere`) is deliberate and fixed by
 * §8.6, which spells them so; the identifier of a KIND in code stays English.
 */

export type ProtocolVariableKind = 'objective' | 'criterion';

export type ProtocolVariable = {
  readonly kind: ProtocolVariableKind;
  readonly id: string;
};

/** The two token prefixes of §8.6, keyed by the English kind. */
const TOKEN_PREFIX: Readonly<Record<ProtocolVariableKind, string>> = {
  objective: 'objectif',
  criterion: 'critere',
};

/**
 * One token, anywhere in a text. Ids carry no brace and no whitespace - they are
 * struck by the editor (uuids) or by the criterion store (uuids too).
 */
const TOKEN = /\{\{(objectif|critere):([^{}\s]+)\}\}/g;

/** `{{objectif:id}}` / `{{critere:id}}` - what the editor inserts and the engine reads. */
export function variableToken(variable: ProtocolVariable): string {
  return `{{${TOKEN_PREFIX[variable.kind]}:${variable.id}}}`;
}

/** Every variable a text carries, in order of appearance, duplicates kept. */
export function variablesOf(text: string): ProtocolVariable[] {
  const found: ProtocolVariable[] = [];
  for (const match of text.matchAll(TOKEN)) {
    const [, prefix, id] = match;
    if (prefix === undefined || id === undefined) continue;
    found.push({ kind: prefix === 'objectif' ? 'objective' : 'criterion', id });
  }
  return found;
}

/**
 * A text split into its literal runs and its variables, for a renderer that
 * draws a chip per token. Empty literal runs are dropped, so two adjacent
 * tokens give two segments and not three.
 */
export type TextSegment =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'variable'; readonly variable: ProtocolVariable };

export function segmentsOf(text: string): TextSegment[] {
  const segments: TextSegment[] = [];
  let at = 0;
  for (const match of text.matchAll(TOKEN)) {
    const [, prefix, id] = match;
    const start = match.index ?? 0;
    if (prefix === undefined || id === undefined) continue;
    if (start > at) segments.push({ kind: 'text', text: text.slice(at, start) });
    segments.push({
      kind: 'variable',
      variable: { kind: prefix === 'objectif' ? 'objective' : 'criterion', id },
    });
    at = start + match[0].length;
  }
  if (at < text.length) segments.push({ kind: 'text', text: text.slice(at) });
  return segments;
}

/**
 * The text with every token replaced by what `resolve` answers for it - the
 * current name for a screen, the matter or the direction for the engine. A
 * token `resolve` cannot answer is left AS IS rather than blanked: §8.6 wants an
 * unresolved variable refused at publication, never rendered empty in silence.
 */
export function renderVariables(
  text: string,
  resolve: (variable: ProtocolVariable) => string | null,
): string {
  return segmentsOf(text)
    .map((segment) => {
      if (segment.kind === 'text') return segment.text;
      return resolve(segment.variable) ?? variableToken(segment.variable);
    })
    .join('');
}

/**
 * Every text of a protocol that may carry a variable (§8.6): the direction of
 * each step, of each objective, and of each « direction d'IA » action. Steps
 * are walked in order so a caller can locate the step a text belongs to.
 */
export type DirectionSite = {
  readonly stepIndex: number;
  readonly stepId: string;
  /** Null when the text is the step's own direction or an action's. */
  readonly objectiveId: string | null;
  readonly text: string;
};

export function directionSitesOf(steps: readonly ProtocolStepDraft[]): DirectionSite[] {
  const sites: DirectionSite[] = [];
  steps.forEach((step, stepIndex) => {
    sites.push({ stepIndex, stepId: step.id, objectiveId: null, text: step.direction });
    for (const objective of step.objectives) {
      sites.push({
        stepIndex,
        stepId: step.id,
        objectiveId: objective.id,
        text: objective.direction,
      });
    }
    for (const condition of step.conditions) {
      for (const action of condition.actions) {
        if (action.kind === 'direction') {
          sites.push({ stepIndex, stepId: step.id, objectiveId: null, text: action.text });
        }
      }
    }
  });
  return sites;
}
